// Fleet Hours of Service Domain Configuration
export const fleethoursofserviceConfig = {
  // Basic domain information
  id: 'fleet-hours-of-service',
  name: 'Fleet Hours of Service',
  tagline: 'Hours of Service Management',
  description: 'Comprehensive hours of service tracking and compliance management.',
  
  // Assets
  logo: '/assets/logos/fleet-hours-of-service-logo',
  whiteLogo: '/assets/logos/fleet-hours-of-service-white-logo',
  favicon: '/assets/favicons/fleet-hours-of-service-favicon.png',
  icon: '/assets/icons/fleet-hours-of-service',
  
  // Navigation
  pages: ['home', 'solutions', 'blog'],
  navigation: [
    { name: 'Home', path: '/' },
    { name: 'Solutions', path: '/solutions' },
    { name: 'Blog', path: '/blog' },
  ],
  
  // CTAs
  cta: {
    primary: 'Request Demo',
    secondary: 'Buy Now'
  },
  
  // Shop link
  shopLink: 'https://shop.fleethoster.com/collections/geotab',
  
  // UI Configuration
  variant: 'corporate',
  showFeatures: true,
  showTestimonials: true,
  
  // Content sections
  hero: {
    title: 'Geotab Fleet Hours of Service',
    subtitle: 'Stay compliant with hours of service regulations while optimizing driver productivity and safety.',
    features: ['HOS reporting', 'Log management', 'Vehicle inspections']
  },
  
  // Gallery/Image Slider configuration
  gallery: {
    title: 'Geotab Fleet Hours of Service Solutions',
    subtitle: 'Explore our comprehensive HOS management and compliance solutions',
    description: 'See how Fleet Hours of Service transforms compliance with advanced tracking technology'
  },

  features: {
    title: 'Geotab Fleet Hours of Service Features',
    subtitle: 'Best practices for fleet success.',
    description: 'Geotab ELD System is a comprehensive HOS Management and DOT Compliance Solutions.',
    items: [
      {
        title: 'Cross border compliance',
        description: '100+ HOS rulesets & exemptions for the U.S. & Canada. Intrastate, short-haul, agriculture, etc.',
        icon: '🌍'
      },
      {
        title: 'Driver ID',
        description: 'Auto-log which drivers use which vehicles. Track driver behaviors & identify unauthorized use.',
        icon: '👤'
      },
      {
        title: 'Offline support for out of cellular coverage use',
        description: 'Work offline and auto-sync data when coverage is restored. Connect our IOX-USB cable for offline ELD.',
        icon: '📡'
      },
      {
        title: 'Simplified roadside inspections',
        description: 'Complete roadside inspections via app. Submit instantly. Includes PIN protection.',
        icon: '🚗'
      }
    ]
  },
  
  solutions: {
    title: 'Geotab Fleet Hours of Service Solutions',
    subtitle: 'Get more value from your ELD with Geotab’s open APIs and Software Development Kit.',
    description: 'Fleet Hours of service management and DOT compliance solutions all in one platform.',
    sections: [
      {
        title: 'HOS monitoring',
        description: "Track drivers' HOS status in real time. View driver status, driving time, remaining hours, and required breaks. All in on one dashboard. Get alerts when drivers are approaching or exceeding HOS limits. Streamline ELD compliance.",
      },
      {
        title: 'Cross border',
        description: "Ensure cross-border compliance between the U.S. and Canada. Auto-switch between 100+ HOS rulesets as drivers cross borders. Intrastate, short-haul, 16-hour workdays, and more. Get real-time alerts for cross-border movements.",
      },
      {
        title: 'Log management',
        description: 'Auto-update logs based on vehicle movements, border crossings, and more. Edit and annotate logs via mobile app. Send drivers reminders to certify logs and claim unassigned driving.',
      },
      {
        title: 'Vehicle inspections',
        description: 'Enable DVIR completion via mobile app. Pre- and post-trip. Create custom inspection checklists based on vehicle types and regulatory requirements. Get alerts for completed inspections and vehicle issues.',
      },
      {
        title: 'Reporting',
        description: 'Auto-generate ELD-compliant reports for driver hours, rest periods, work limits, and more. Keep detailed audit trails, including driver activity and sleeper berth status. Get all your data at your fingertips and streamline audits.',
      },
    ]
  },
  
  
  testimonials: [
    {
      content: "The platform is easy to use and very driver friendly. It has helped us improve driver safety and efficiency, and has given us valuable insights into our operations.",
      author: "Jennifer Y.",
      role: "DOT Compliance Manager II",
      company: "",
      avatar: "https://randomuser.me/api/portraits/women/48.jpg"
    },
    {
      content: "We will continue to use the scorecard and other telematics provided by Geotab to change the culture of safety within our company.",
      author: "Daryn Rabb",
      role: "Health and Safety Manager",
      company: "",
      avatar: "https://randomuser.me/api/portraits/men/12.jpg"
    },
    {
      content: "Our cmpany has been with 2 other ELD platforms, but Geotab has been by far the best for the money.",
      author: "Jeff M.",
      role: "VP, Safety",
      company: "",
      avatar: "https://randomuser.me/api/portraits/men/49.jpg"
    }
  ],

  blog: {
    title: 'Fleet Hours of Service Blog',
    subtitle: 'Latest trends and insights in fleet management technology',
    description: 'Stay informed with the latest updates about the industry.',
  },
  
  // SEO and Meta
  seo: {
    // Basic Meta Tags
    title: 'Fleet Hours of Service - HOS Management & DOT Compliance',
    description: 'Advanced HOS tracking and compliance management to keep fleets DOT compliant, improve productivity, and simplify regulatory reporting.',
    keywords: [
      'hours of service', 'fleet compliance', 'driver management', 'DOT compliance', 'fleet tracking',
      'HOS management', 'driver hours tracking', 'fleet compliance software', 'DOT regulations', 'driver productivity',
      'fleet safety compliance', 'hours of service software', 'driver management system', 'fleet regulatory compliance',
      'HOS tracking', 'fleet compliance management', 'driver hours management', 'fleet safety regulations', 'Geotab ELD System', 
      'Geotab', 'Geotab solutions', 'Geotab ELD', 'Geotab ELD System', 'Geotab ELD solutions'
    ],
    
    // Open Graph & Social Media
    ogTitle: 'Fleet Hours of Service - Professional HOS Management Solutions',
    ogDescription: 'Comprehensive hours of service tracking and compliance management. Stay compliant, optimize productivity.',
    ogType: 'website',
    ogUrl: 'https://www.fleethoursofservice.com',
    
    // Twitter Cards
    twitterCard: 'summary_large_image',
    twitterTitle: 'Fleet Hours of Service - Professional HOS Management Solutions',
    twitterDescription: 'Comprehensive hours of service tracking and compliance management. Stay compliant, optimize productivity.',
    
    // Robots
    robots: 'index, follow',
    author: 'Fleet Hours of Service',
    publisher: 'Fleet Hours of Service',
    
    // Page-Specific SEO
    pages: {
      home: {
        title: 'Fleet Hours of Service - HOS Management & DOT Compliance',
        description: 'Advanced HOS tracking and compliance management to keep fleets DOT compliant, improve productivity, and simplify regulatory reporting.',
        keywords: ['hours of service', 'fleet compliance', 'driver management', 'DOT compliance', 'fleet tracking']
      },
      solutions: {
        title: 'Fleet Hours of Service Solutions - HOS Management Services',
        description: 'Comprehensive HOS management solutions and compliance services. Professional driver management and regulatory compliance tools.',
        keywords: ['HOS management solutions', 'fleet compliance services', 'driver management', 'regulatory compliance']
      },
      products: {
        title: 'Fleet Hours of Service - HOS Management Products & Tools',
        description: 'Professional HOS management software and compliance tools. Driver tracking, hours management, and regulatory compliance solutions.',
        keywords: ['HOS management software', 'compliance tools', 'driver tracking software', 'hours management']
      },
      blog: {
        title: 'Fleet Hours of Service News & Insights - HOS Compliance Blog',
        description: 'Latest news, insights, and best practices for hours of service management and fleet compliance. Professional tips and regulatory updates.',
        keywords: ['HOS management news', 'fleet compliance insights', 'driver management tips', 'regulatory compliance best practices']
      }
    },
    
    // Performance & Technical SEO
    technical: {
      pageLoadTime: '< 1 second',
      mobileOptimized: true,
      sslEnabled: true,
      schemaMarkup: true,
      xmlSitemap: true,
      robotsTxt: true,
      compressionEnabled: true,
      imageOptimization: true,
      cdnEnabled: true
    },

    // Local Business Info for Structured Data
    localBusiness: {
      streetAddress: '',
      addressLocality: 'Atlanta',
      addressRegion: 'GA',
      postalCode: '30308',
      addressCountry: 'US'
    }
  }
};
